import React, { useState, useEffect, useRef } from 'react';
import AppLayout from './components/AppLayout'; 
import Dashboard from './pages/Dashboard';
import NewStory from './pages/NewStory';
import Archive from './pages/Archive';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Login from './pages/Login';
import { supabase } from './services/supabaseClient';
import { logAction } from './services/store'; // Import logging service
import { Session } from '@supabase/supabase-js';
import { Loader2, ShieldAlert } from 'lucide-react';

// --- CONFIGURATION ---
// Set this to your organization's domain to auto-block others. 
// Set to null to allow any domain (and rely on manual role assignment).
const RESTRICT_TO_DOMAIN = null; // e.g., 'tolonews.com'

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [currentView, setCurrentView] = useState('queue');
  const [activeStoryId, setActiveStoryId] = useState<string | undefined>(undefined);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Security & Loading States
  const [isAccessChecking, setIsAccessChecking] = useState(false);
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  
  // Ref to prevent double-logging in React 18 StrictMode
  const hasLoggedLogin = useRef(false);

  useEffect(() => {
    // 1. Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) checkAccessAndLog(session);
    });

    // 2. Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) checkAccessAndLog(session);
      else {
        // Reset state on logout
        hasLoggedLogin.current = false;
        setAccessDenied(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- SECURITY & LOGGING LOGIC ---
  const checkAccessAndLog = async (currentSession: Session) => {
    if (hasLoggedLogin.current) return; // Prevent double execution
    setIsAccessChecking(true);
    hasLoggedLogin.current = true;

    try {
      const user = currentSession.user;
      
      // A. DOMAIN CHECK (Optional)
      if (RESTRICT_TO_DOMAIN) {
        const emailDomain = user.email?.split('@')[1];
        if (emailDomain !== RESTRICT_TO_DOMAIN) {
          setAccessDenied(`Access restricted to @${RESTRICT_TO_DOMAIN} emails only.`);
          await supabase.auth.signOut();
          setIsAccessChecking(false);
          return;
        }
      }

      // B. PROFILE/ROLE CHECK
      // Fetch user profile to check if suspended
      const { data: profile } = await supabase
        .from('profiles')
        .select('role, full_name')
        .eq('id', user.id)
        .single();

      if (profile?.role === 'suspended') {
        setAccessDenied("Your account has been suspended by an administrator.");
        await supabase.auth.signOut();
        setIsAccessChecking(false);
        return;
      }

      // C. LOCATION TRACKING & LOGGING
      // Fetch location data from public API
      let locationDetails = "Location unknown";
      try {
        const geoRes = await fetch('https://ipapi.co/json/');
        if (geoRes.ok) {
          const geoData = await geoRes.json();
          // Construct a readable location string
          locationDetails = `IP: ${geoData.ip}, Location: ${geoData.city}, ${geoData.country_name} (${geoData.org})`;
        }
      } catch (e) {
        console.warn("Could not fetch location data", e);
      }

      // Log to Supabase audit_logs
      await logAction(
        user.id, 
        "Logged In", 
        locationDetails
      );

    } catch (error) {
      console.error("Access check failed:", error);
    } finally {
      setIsAccessChecking(false);
    }
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  const handleNavigate = (view: string) => {
    setCurrentView(view);
    if (view !== 'new_story') {
      setActiveStoryId(undefined);
    }
  };

  const handleEditStory = (id: string) => {
    setActiveStoryId(id);
    setCurrentView('new_story');
  };

  const handleNewStory = () => {
    setActiveStoryId(undefined);
    setCurrentView('new_story');
  };

  // --- RENDER STATES ---

  // 1. Loading Access Check
  if (session && isAccessChecking) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
        <p className="text-slate-500">Verifying credentials & location...</p>
      </div>
    );
  }

  // 2. Access Denied Screen
  if (accessDenied) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-red-50 dark:bg-slate-950 p-4">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-lg max-w-md text-center border border-red-100 dark:border-red-900">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="h-8 w-8 text-red-600 dark:text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{accessDenied}</p>
          <button 
            onClick={() => setAccessDenied(null)}
            className="px-4 py-2 bg-slate-900 dark:bg-slate-800 text-white rounded-lg hover:opacity-90"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // 3. Not Logged In
  if (!session) {
    return <Login />;
  }

  // 4. Main App (Render content based on current view)
  const renderContent = () => {
    switch (currentView) {
      case 'queue':
        return <Dashboard onEditStory={handleEditStory} onNewStory={handleNewStory} />;
      case 'new_story':
        return (
          <NewStory 
            storyId={activeStoryId} 
            onExit={() => handleNavigate('queue')} 
          />
        );
      case 'archive':
        return <Archive />;
      case 'admin':
        return <Admin />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard onEditStory={handleEditStory} onNewStory={handleNewStory} />;
    }
  };

  const isFocusMode = currentView === 'new_story';

  return (
    <AppLayout
      currentView={currentView}
      onChangeView={handleNavigate}
      isDarkMode={isDarkMode}
      toggleTheme={toggleTheme}
      showSidebar={!isFocusMode}
    >
      {renderContent()}
    </AppLayout>
  );
};

export default App;